import { utcToZonedTime } from 'date-fns-tz';
import endOfQuarter from 'date-fns/endOfQuarter';
import isSameMonth from 'date-fns/isSameMonth';
import isSameYear from 'date-fns/isSameYear';
import isThisYear from 'date-fns/isThisYear';
import startOfQuarter from 'date-fns/startOfQuarter';
import { useIntl, type IntlShape } from 'react-intl';

import {
	DIFF_YEAR_FORMAT,
	FULL_DATE_FORMAT,
	FULL_NICE_FORMAT,
	ISO_LOCAL_DATE,
	ISO_LOCAL_DATE_TIME,
	ISO_LOCAL_DATE_WITH_SLASHES,
	MONTH_ONLY_FORMAT,
	MONTH_ONLY_FORMAT_DIFF_YEAR,
	MONTH_ONLY_FORMAT_SHORT,
	MONTH_WITH_YEAR,
	NICE_DATE_TIME,
	SAME_MONTH_FORMAT,
	SAME_YEAR_FORMAT,
} from './date-constants';
import { differenceInCalendarWeeks, format, isSameWeek } from './date-fns-mixins';
import { tzDate } from './date-utils';

export const formatDateRange = (from: Date, to: Date, locale: Locale, separator = '-'): string => {
	if (isSameYear(from, to)) {
		const fromDisplay = format(from, isSameMonth(from, to) ? SAME_MONTH_FORMAT : SAME_YEAR_FORMAT, {
			locale,
		});
		const toDisplay = formatNiceDate(to, locale);
		return `${fromDisplay}${separator}${toDisplay}`;
	} else {
		const fromDisplay = format(from, DIFF_YEAR_FORMAT, { locale });
		const toDisplay = format(to, DIFF_YEAR_FORMAT, { locale });
		return `${fromDisplay}${separator}${toDisplay}`;
	}
};

export const formatFullNiceDate = (date: Date, _locale: Locale, noDash?: boolean): string => {
	return format(date, noDash ? DIFF_YEAR_FORMAT : FULL_NICE_FORMAT);
};

export const formatNiceMonthDate = (date: Date, locale: Locale): string =>
	formatWithLocale(
		date,
		isThisYear(date) ? MONTH_ONLY_FORMAT : MONTH_ONLY_FORMAT_DIFF_YEAR,
		locale,
	);

export const formatTooltipNiceMonthDate = (date: Date, locale: Locale): string =>
	formatWithLocale(date, MONTH_WITH_YEAR, locale);

/**
 * Format interval label with fiscal year (2-digit), (e.g. "Period 2 FY26", "Q1 FY26").
 */
export const formatNiceIntervalDate = (
	fiscalYearPrefix: string,
	intervalName: string,
	date: Date,
	locale: Locale,
): string => {
	const year = formatWithLocale(date, 'yy', locale);
	return `${intervalName} ${fiscalYearPrefix}${year}`;
};

/**
 * Format an interval range display name with fiscal year info.
 *
 * For a single interval or same-year range: "Period 1 – Period 3 FY26"
 * For a cross-year range (intervalDisplayName contains " – "):
 *   splits on " – " and appends FY to each part: "Period 1 FY26 – Period 3 FY27"
 * Falls back to formatNiceIntervalDate for non-range names.
 */
export const formatNiceIntervalRangeDate = (
	fiscalYearPrefix: string,
	intervalDisplayName: string,
	startDate: Date,
	endDate: Date,
	locale: Locale,
): string => {
	const startYear = formatWithLocale(startDate, 'yy', locale);
	const endYear = formatWithLocale(endDate, 'yy', locale);

	const rangeParts = intervalDisplayName.split(' – ');
	if (rangeParts.length === 2) {
		if (startYear === endYear) {
			return `${intervalDisplayName} ${fiscalYearPrefix}${startYear}`;
		}
		return `${rangeParts[0]} ${fiscalYearPrefix}${startYear} – ${rangeParts[1]} ${fiscalYearPrefix}${endYear}`;
	}

	return formatNiceIntervalDate(fiscalYearPrefix, intervalDisplayName, startDate, locale);
};

export const formatNiceQuarterDate = (date: Date, locale: Locale) =>
	`${formatWithLocale(startOfQuarter(date), MONTH_ONLY_FORMAT_SHORT, locale)}-${formatWithLocale(
		endOfQuarter(date),
		isThisYear(date) ? MONTH_ONLY_FORMAT_SHORT : MONTH_ONLY_FORMAT_DIFF_YEAR,
		locale,
	)}`;

export const formatTooltipNiceQuarterDate = (date: Date, locale: Locale) =>
	`${formatWithLocale(startOfQuarter(date), MONTH_ONLY_FORMAT, locale)}-${formatWithLocale(
		endOfQuarter(date),
		MONTH_WITH_YEAR,
		locale,
	)}`;

export const formatNiceDateString = (dateString: string, locale: Locale): string => {
	return formatNiceDate(tzDate(dateString), locale);
};

export const formatNiceDate = (date: Date, locale: Locale): string =>
	formatWithLocale(date, isThisYear(date) ? SAME_YEAR_FORMAT : DIFF_YEAR_FORMAT, locale);

export const formatNiceDateTooltip = (date: Date, locale: Locale): string =>
	formatWithLocale(date, FULL_DATE_FORMAT, locale);

export const formatWithLocale = (date: Date, dateFormat: string, locale: Locale): string =>
	format(date, dateFormat, { locale });

export const formatNiceDateTime = (date: Date, locale: Locale): string =>
	formatWithLocale(date, NICE_DATE_TIME, locale);

export const formatIsoLocateDateWithSlashes = (date: Date, locale: Locale): string =>
	format(date, ISO_LOCAL_DATE_WITH_SLASHES, { locale });

export const formatMonthOnlyDate = (date: Date, locale: Locale): string =>
	formatWithLocale(date, MONTH_ONLY_FORMAT, locale);

/**
 * TODO: Both of the methods below are used in constructions outside of React components.
 * They will need special consideration when we want to enforce the `locale` parameter.
 */

export const formatIsoLocalDate = (date: Date, locale: Locale): string =>
	format(date, ISO_LOCAL_DATE, { locale });
export const formatIsoLocalDateTime = (date: Date, locale: Locale): string =>
	format(utcToZonedTime(date, 'UTC', { locale }), ISO_LOCAL_DATE_TIME, { locale });

/**
 * Format display text for a given interval using a week-window granularity against an "anchor" date
 * @param intl
 * @param start of interval
 * @param end of interval
 * @param anchorDate used as relative date, at which calculation will be performed against. If in doubt, leave empty
 * @param locale
 */
export const formatWeekRange = (
	intl: IntlShape,
	start: Date,
	end: Date,
	anchorDate: Date | undefined = tzDate(),
	locale: Locale,
): string => {
	if (isSameWeek(anchorDate, start, { locale })) {
		return intl.formatMessage({
			id: 'townsquare.date-utils.week-range.this-week',
			description: 'This week section title',
			defaultMessage: 'This week',
		});
	} else if (isLastWeekOf(anchorDate, end, locale)) {
		return intl.formatMessage({
			id: 'townsquare.date-utils.week-range.last-week',
			description: 'Last week section title',
			defaultMessage: 'Last week',
		});
	} else {
		return formatDateRange(start, end, locale);
	}
};

/**
 * Format display text for a given interval using a week-window granularity against an "anchor" date
 * @param start of interval
 * @param end of interval
 * @param anchorDate used as relative date, at which calculation will be performed against. If in doubt, leave empty
 * @param locale provided as a param to avoid a circular dependency
 */
export const useFormatWeekRange = (
	start: Date,
	end: Date,
	anchorDate: Date | undefined = tzDate(),
	locale: Locale,
): string => {
	const intl = useIntl();

	return formatWeekRange(intl, start, end, anchorDate, locale);
};

/**
 * Is the older date last week of the newer date?
 */
export function isLastWeekOf(newer: Date, older: Date, locale: Locale): boolean {
	return differenceInCalendarWeeks(newer, older, { locale }) === 1;
}
